import type { EntityAccessor, EnumFilterArtifact, FilterArtifact, FilterHandler } from '@contember/bindx'
import type { EntityRef } from '@contember/bindx'
import { ColumnLeaf, type ColumnLeafProps } from '@contember/bindx-dataview'
import { Field, HasMany, useHasMany, withCollector } from '@contember/bindx-react'
import { CheckIcon } from 'lucide-react'
import { createElement, type ReactElement, type ReactNode } from 'react'
import type { GdSubscriber } from '../admin-schema'
import { formatCzechDate } from '../lib/format-date'
import { Badge } from './status-badge'

/**
 * Access is derived, never stored: a subscriber is active while some paid order's period covers
 * today. Same rule as `activeMemberFilter` (worker gd/engine/audience.ts) and the dashboard
 * count — a future grace window changes there, not here.
 *
 * One module for the listing cell and the detail's header badge, because the two are read
 * against each other: "Aktivní" in the row and nothing at the top of the record it opens is how
 * an operator ends up trusting neither.
 */

interface OrderPeriod {
	status: string | null
	periodStart: string | null
	periodEnd: string | null
}

export interface SubscriberState {
	label: 'Aktivní' | 'Neaktivní'
	tone: 'success' | undefined
	/** The date the state hangs on, already phrased — „do 31. 12. 2027", „vypršelo 4. 3. 2025". */
	note: string | undefined
}

/**
 * The date is as much the point as the badge: "Aktivní" on a customer everyone knows stopped
 * paying is a migrated order carrying a legacy `gd_prepaid_to` in the future, and reading the
 * orders was the only way to see that.
 */
export function subscriberState(orders: readonly OrderPeriod[], today: string): SubscriberState {
	const paid = orders.filter(order => order.status === 'paid')

	const liveUntil = latest(paid.filter(order => covers(order, today)).map(order => order.periodEnd))
	if (liveUntil !== null) return { label: 'Aktivní', tone: 'success', note: `do ${formatCzechDate(liveUntil)}` }

	// A period that has not started yet is not access — but it is the answer to „why is this one
	// grey, they just paid", so the row says so.
	const startsOn = earliest(paid.map(order => order.periodStart).filter(start => start !== null && start > today))
	if (startsOn !== null) return { label: 'Neaktivní', tone: undefined, note: `od ${formatCzechDate(startsOn)}` }

	const expiredOn = latest(paid.map(order => order.periodEnd).filter(end => end !== null && end < today))
	return { label: 'Neaktivní', tone: undefined, note: expiredOn === null ? undefined : `vypršelo ${formatCzechDate(expiredOn)}` }
}

export function pragueToday(): string {
	return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date())
}

/**
 * The listing cell: badge with its date beneath, so the column stays as narrow as the badge.
 * The row's order fields must already be in the grid's selection — see `subscribers.tsx`.
 */
export function SubscriberStateCell({ subscriber }: { subscriber: EntityAccessor<GdSubscriber> }): ReactElement {
	const orders = useHasMany(subscriber.orders)
	return <StateBadge layout="stacked" state={subscriberState(orders.items.map(readOrder), pragueToday())} />
}

/**
 * The same state in the detail's page header, on one line. It is the first thing the record has
 * to answer — the orders that decide it sit at the bottom of a long form.
 */
export const SubscriberStateHeaderBadge = withCollector(function SubscriberStateHeaderBadge(
	{ subscriber }: { subscriber: EntityRef<GdSubscriber> },
) {
	const orders = useHasMany(subscriber.orders)
	return <StateBadge layout="inline" state={subscriberState(orders.items.map(readOrder), pragueToday())} />
}, ({ subscriber }) => (
	<HasMany field={subscriber.orders}>
		{order => (
			<>
				<Field field={order.status} />
				<Field field={order.periodStart} />
				<Field field={order.periodEnd} />
			</>
		)}
	</HasMany>
))

function StateBadge({ state, layout }: { state: SubscriberState; layout: 'stacked' | 'inline' }): ReactElement {
	return (
		<span className={layout === 'stacked' ? 'inline-flex flex-col items-start gap-0.5' : 'inline-flex items-center gap-2'}>
			<Badge tone={state.tone}>{state.label}</Badge>
			{
				/* In the header the date is a headline fact, so it carries the brand blue of the title
				   beside it; in the listing it stays the quiet grey of a dense column. */
			}
			{state.note
				? (
					<span className={`whitespace-nowrap tabular-nums ${layout === 'stacked' ? 'text-[11px] text-gray' : 'text-sm text-blue-primary'}`}>
						{state.note}
					</span>
				)
				: null}
		</span>
	)
}

const readOrder = (order: {
	status: { value: string | null }
	periodStart: { value: string | null }
	periodEnd: { value: string | null }
}): OrderPeriod => ({ status: order.status.value, periodStart: order.periodStart.value, periodEnd: order.periodEnd.value })

const covers = (order: OrderPeriod, today: string): boolean =>
	order.periodStart !== null && order.periodStart <= today && order.periodEnd !== null && order.periodEnd >= today

function latest(values: readonly (string | null | undefined)[]): string | null {
	return values.reduce<string | null>((best, value) => value != null && (best === null || value > best) ? value : best, null)
}

function earliest(values: readonly (string | null | undefined)[]): string | null {
	return values.reduce<string | null>((best, value) => value != null && (best === null || value < best) ? value : best, null)
}

/**
 * The listing's „Stav" column, with the filter the header would otherwise have nothing to open.
 *
 * A hand-rolled `ColumnLeaf` rather than one of the stock columns: those derive their filter from
 * the field they render, and this state is not a field — it is a question about the subscriber's
 * orders. The same shape the badge is computed from is therefore also expressed as a where clause
 * ({@link livePaidOrderFilter}), and the two must keep saying the same thing.
 *
 * No sorting: ordering by a state Postgres never stores would mean sorting in the page, which is
 * worse than not offering it — the grid is paginated and would only sort the rows on screen.
 */
export function SubscriberStateColumn(_props: { header?: ReactNode }): null {
	return null
}

SubscriberStateColumn.staticRender = (props: Record<string, unknown>): ReactNode => {
	const leafProps: ColumnLeafProps = {
		columnType: 'action',
		name: STATE_FILTER_NAME,
		fieldName: null,
		fieldRef: null,
		sortingField: null,
		filterName: STATE_FILTER_NAME,
		filterHandler: stateFilterHandler as FilterHandler<FilterArtifact>,
		isTextSearchable: false,
		header: (props.header as ReactNode) ?? 'Stav',
		// The cell reads fields no column selects, so the column asks for them itself — the route
		// used to carry that line by hand, which is exactly the sort of thing that rots.
		collectSelection: collector => {
			const subscriber = collector as EntityAccessor<GdSubscriber>
			subscriber.orders.map(order => [order.status.value, order.periodStart.value, order.periodEnd.value])
		},
		renderCell: accessor => createElement(SubscriberStateCell, { subscriber: accessor as EntityAccessor<GdSubscriber> }),
		renderFilter: ({ artifact, setArtifact }) =>
			createElement(StateFilterControls, {
				artifact: artifact as EnumFilterArtifact | undefined,
				setArtifact: setArtifact as (next: EnumFilterArtifact) => void,
			}),
	}

	return createElement(ColumnLeaf, leafProps)
}

const STATE_FILTER_NAME = 'state'

/** „Has a paid order whose period covers today" — the badge's rule, as a Contember where clause. */
const livePaidOrderFilter = (today: string) => ({
	orders: { status: { eq: 'paid' }, periodStart: { lte: today }, periodEnd: { gte: today } },
})

/**
 * Stored as an `EnumFilterArtifact` (`{ values: ['active'] }`) purely so the artifact stays inside
 * bindx's own union — it is what the grid persists to `localStorage` and hands back on reload.
 */
const stateFilterHandler: FilterHandler<EnumFilterArtifact> = {
	defaultArtifact: () => ({}),
	// Every read of the artifact tolerates it being missing entirely: the grid persists its whole
	// filter object per screen and restores it as `stored ?? defaults` — no merging — so anyone who
	// had ever touched a filter here carries an object written before this column existed, and the
	// state filter comes back `undefined` rather than empty. See the same trap under
	// `URL_COLUMN_HIDDEN` in `admin-data-grid.tsx`.
	isActive: artifact => (artifact?.values?.length ?? 0) > 0,
	toWhere: artifact => {
		const wanted = artifact?.values?.[0]
		if (wanted !== 'active' && wanted !== 'inactive') return undefined
		// Read at query time, not at module load: an admin left open overnight must not keep
		// filtering by yesterday.
		const live = livePaidOrderFilter(pragueToday())
		return wanted === 'active' ? live : { not: live }
	},
}

const STATE_OPTIONS = [
	{ value: 'active', label: 'Aktivní', tone: 'success' as const },
	{ value: 'inactive', label: 'Neaktivní', tone: undefined },
]

/** Picking the state that is already on clears the filter — same gesture in and out. */
function StateFilterControls(
	{ artifact, setArtifact }: { artifact: EnumFilterArtifact | undefined; setArtifact: (next: EnumFilterArtifact) => void },
): ReactElement {
	const current = artifact?.values?.[0]

	return (
		<div className="flex flex-col gap-1">
			{STATE_OPTIONS.map(option => (
				<button
					className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left hover:bg-light-gray"
					key={option.value}
					onClick={() => setArtifact(current === option.value ? {} : { values: [option.value] })}
					type="button"
				>
					<Badge tone={option.tone}>{option.label}</Badge>
					{current === option.value ? <CheckIcon className="size-3.5 text-blue-primary" /> : null}
				</button>
			))}
		</div>
	)
}
